// ABOUTME: React context for managing shop selection state across the application.
// ABOUTME: Provides shop list, current shop selection, and shop switching functionality.

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

const ShopContext = createContext({});

export const useShop = () => useContext(ShopContext);

export const ShopProvider = ({ children }) => {
  const [shops, setShops] = useState([]);
  const [currentShopId, setCurrentShopId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load shops from API
  const loadShops = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('lipoti_token');
      const response = await axios.get(`${API_BASE_URL}/shops/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const shopsList = response.data;
      setShops(shopsList);

      // Load last selected shop from localStorage (only if valid)
      const savedShopId = localStorage.getItem('selected_shop');

      // Only select a shop if it was previously saved and still exists
      if (savedShopId && shopsList.find(s => s.shop_id === savedShopId)) {
        setCurrentShopId(savedShopId);
      }
      // Otherwise, leave currentShopId as null - no auto-selection
    } catch (err) {
      console.error('Failed to load shops:', err);
      setError('Failed to load shops');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const switchShop = useCallback((shopId) => {
    if (shopId === currentShopId) {
      return; // Already on this shop
    }

    localStorage.setItem('selected_shop', shopId);
    // Full page reload to ensure clean state
    window.location.href = `/shops/${shopId}/dashboard`;
  }, [currentShopId]);

  // Get current shop config object
  const currentShop = shops.find(s => s.shop_id === currentShopId) || null;

  return (
    <ShopContext.Provider value={{
      shops,
      currentShopId,
      currentShop,
      switchShop,
      loading,
      error,
      reloadShops: loadShops
    }}>
      {children}
    </ShopContext.Provider>
  );
};

export default ShopContext;